import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SoilCropNutritionService } from '../../services/soil-crop-nutrition.service';

interface CropDefinition {
  id: number;
  cultivar: string | null;
  cultivarSize: number | null;
}

interface SeasonField {
  fieldId: number;
  fieldName: string;
  farmId: number;
  farmName: string;
  definitions: CropDefinition[];
  selected: boolean;
  /** Which definitions this field contributes. Populated even for a single cultivar. */
  selectedDefinitionIds: number[];
}

interface FarmGroup {
  farmId: number;
  farmName: string;
  fields: SeasonField[];
}

interface SeasonBlock {
  id: number;
  name: string;
  farms: FarmGroup[];
  /** How many fields exist in this season at all, selected or not. */
  available: number;
}

/**
 * Create a Soil Crop Nutrition recommendation, season first.
 *
 * <h3>Why this exists</h3>
 *
 * The live screen asks for fields and then asks which season each one belongs to. That ordering
 * creates three problems it then has to work around:
 *
 * <ul>
 *   <li>A field with no season can be ticked. It shows an empty dropdown, and at create time it is
 *       still sent, producing a block with no season and so no crop definition. Nothing says so.</li>
 *   <li>Two steps and an "Apply to all fields" control exist only to undo the ordering. Choosing
 *       the season first IS applying it to all.</li>
 *   <li>Every tick fires its own request for that one field's seasons.</li>
 * </ul>
 *
 * Asking for the season first removes all three. You browse what is IN a season, so a field with
 * none cannot be chosen by accident, there is nothing left to assign afterwards, and the seasons
 * for every field arrive in one call.
 *
 * <h3>One season per field, which is the API's rule not ours</h3>
 *
 * The create payload is keyed by field: <code>fieldSeasons: Record&lt;fieldId, seasonId&gt;</code>.
 * So "multiple seasons" means different fields may use different seasons, never the same field
 * twice. A field already taken under one season is therefore shown disabled under the others, with
 * the reason, rather than being silently dropped at create time.
 *
 * <h3>It replaced a field-first screen, 22/10/2026</h3>
 *
 * Approved by the team and the client after both were put side by side. The old screen asked for
 * fields and then asked again which season each one belonged to; the two steps and the Apply to
 * all control that joined them are both gone. The create payload is unchanged, so nothing behind
 * this moved.
 */
@Component({
  selector: 'app-create-soil-crop-nutrition',
  standalone: false,
  templateUrl: './create-soil-crop-nutrition.component.html',
  styleUrls: ['./create-soil-crop-nutrition.component.css'],
})
export class CreateSoilCropNutritionComponent implements OnChanges {
  /** The client chosen by the Create Recommendation page. This prototype is embedded only. */
  @Input() client: any = null;
  @Input() stepOffset = 0;

  loading = false;
  creating = false;

  /** Every season that at least one of this client's fields belongs to, most relevant first. */
  allSeasons: { id: number; name: string; available: number; startDate?: string; endDate?: string }[] = [];

  /** The seasons added to this recommendation, in the order they were added. */
  blocks: SeasonBlock[] = [];

  /** Fields that belong to no season at all. Named rather than silently dropped. */
  orphanFields: { farmName: string; fieldName: string }[] = [];

  /**
   * The same fields grouped by farm, which is how somebody would go and fix them.
   *
   * A flat list of forty names tells you there is a problem and nothing about where it is. Grouped,
   * it reads as a to-do list per farm, in the order Season Manager presents them.
   */
  orphanFarms: { farmName: string; fields: string[] }[] = [];

  /** Collapsed by default: it is a footnote, not the job. */
  showOrphans = false;

  /** fieldId to the season that has claimed it, so the others can disable it. */
  private claimedBy = new Map<number, number>();

  private seasonsByField = new Map<number, {
    id: number;
    name: string;
    startDate?: string;
    endDate?: string;
    definitions?: CropDefinition[];
  }[]>();
  private farms: { id: number; name: string; fields: { id: number; name: string }[] }[] = [];

  constructor(
    private readonly service: SoilCropNutritionService,
    private readonly router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['client']) {
      this.load();
    }
  }

  // === Loading ===

  private load(): void {
    const tenantId = this.client?.tenant?.id;
    this.reset();
    if (!tenantId) {
      return;
    }

    this.loading = true;

    this.service
      .getTenantFarmsFields(tenantId)
      .pipe(catchError(() => of([])))
      .subscribe((farms: any[]) => {
        // meta/tenant-farms-fields returns farms with { id, name, fields: [{ id, name }] }. The
        // old field-first screen read exactly these keys, which is the only proof of the shape
        // there is, so they are read the same way here rather than guessed at again.
        this.farms = (farms || []).map(f => ({
          id: f.id,
          name: f.name,
          fields: (f.fields || []).map((field: any) => ({ id: field.id, name: field.name })),
        }));

        // Not flatMap: this project targets a lib below ES2019 and does not have it.
        const fieldIds: number[] = [];
        for (const farm of this.farms) {
          for (const field of farm.fields) {
            fieldIds.push(field.id);
          }
        }
        if (fieldIds.length === 0) {
          this.loading = false;
          return;
        }

        // ONE call for every field, not one per field. The endpoint already took an array.
        forkJoin({ seasons: this.service.getFieldSeasons(fieldIds).pipe(catchError(() => of([]))) })
          .subscribe(({ seasons }) => {
            this.indexSeasons(seasons as any[]);
            this.buildSeasonList();
            this.loading = false;
          });
      });
  }

  private reset(): void {
    this.allSeasons = [];
    this.blocks = [];
    this.orphanFields = [];
    this.orphanFarms = [];
    this.showOrphans = false;
    this.claimedBy.clear();
    this.seasonsByField.clear();
    this.farms = [];
  }

  /** The response is per field. The id key has been seen as both fieldId and id, so accept either. */
  private indexSeasons(rows: any[]): void {
    for (const row of rows ?? []) {
      const fieldId = row?.fieldId ?? row?.id;
      if (fieldId != null) {
        this.seasonsByField.set(Number(fieldId), row?.seasons ?? []);
      }
    }
  }

  private buildSeasonList(): void {
    const counts = new Map<number, { id: number; name: string; available: number; startDate?: string; endDate?: string }>();

    for (const farm of this.farms) {
      for (const field of farm.fields) {
        const seasons = this.seasonsByField.get(field.id) ?? [];

        if (seasons.length === 0) {
          this.orphanFields.push({ farmName: farm.name, fieldName: field.name });
          continue;
        }

        for (const season of seasons) {
          const existing = counts.get(season.id);
          if (existing) {
            existing.available++;
          } else {
            counts.set(season.id, {
              id: season.id,
              name: season.name,
              available: 1,
              startDate: season.startDate,
              endDate: season.endDate,
            });
          }
        }
      }
    }

    this.allSeasons = Array.from(counts.values()).sort((a, b) => this.byRelevance(a, b));

    // Grouped in farm order, which is the order they were collected, so the list matches the
    // layout above it rather than re-sorting into something unfamiliar.
    const byFarm = new Map<string, string[]>();
    for (const orphan of this.orphanFields) {
      const existing = byFarm.get(orphan.farmName);
      if (existing) {
        existing.push(orphan.fieldName);
      } else {
        byFarm.set(orphan.farmName, [orphan.fieldName]);
      }
    }
    this.orphanFarms = Array.from(byFarm, ([farmName, fields]) => ({ farmName, fields }));
  }

  /**
   * Most relevant season first: the one running today, then the nearest upcoming, then the most
   * recently ended.
   *
   * <p>Alphabetical is no use here. Season names look like "2026 Summer" and the one somebody wants
   * is almost always the one they are standing in, not the one that sorts first.
   *
   * <p>This is the precedence Season Manager already applies in
   * FieldDetailsRepository.findMostRelevantByField when it decides which season a field screen
   * opens on. Copied rather than invented, so the two agree about which season a person is
   * probably working in.
   *
   * <p>A season with no dates sorts with the ended ones. It cannot be running, and calling it
   * upcoming would promote a broken record above a real one.
   */
  private byRelevance(
    a: { startDate?: string; endDate?: string; name: string },
    b: { startDate?: string; endDate?: string; name: string },
  ): number {
    const rankA = this.relevanceRank(a);
    const rankB = this.relevanceRank(b);
    if (rankA !== rankB) {
      return rankA - rankB;
    }

    // Upcoming: the one starting soonest leads. Everything else: the one that ended last leads.
    if (rankA === 1) {
      return this.time(a.startDate) - this.time(b.startDate);
    }

    const ended = this.time(b.endDate) - this.time(a.endDate);
    return ended !== 0
      ? ended
      : a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  }

  /** 0 running, 1 upcoming, 2 ended or undated. */
  private relevanceRank(season: { startDate?: string; endDate?: string }): number {
    const now = Date.now();
    const start = this.time(season.startDate);
    const end = this.time(season.endDate);

    if (start && end && start <= now && end >= now) {
      return 0;
    }
    if (start && start > now) {
      return 1;
    }
    return 2;
  }

  private time(value?: string): number {
    if (!value) {
      return 0;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  // === Seasons on the recommendation ===

  get addableSeasons(): { id: number; name: string; available: number; startDate?: string; endDate?: string }[] {
    const added = new Set(this.blocks.map(b => b.id));
    return this.allSeasons.filter(s => !added.has(s.id));
  }

  addSeason(seasonId: number): void {
    if (seasonId == null || this.blocks.some(b => b.id === seasonId)) {
      return;
    }

    const meta = this.allSeasons.find(s => s.id === seasonId);
    if (!meta) {
      return;
    }

    const farms: FarmGroup[] = [];
    let available = 0;

    for (const farm of this.farms) {
      const fields: SeasonField[] = [];

      for (const field of farm.fields) {
        const season = (this.seasonsByField.get(field.id) ?? []).find(s => s.id === seasonId);
        if (!season) {
          continue;
        }
        available++;
        fields.push({
          fieldId: field.id,
          fieldName: field.name,
          farmId: farm.id,
          farmName: farm.name,
          definitions: season.definitions ?? [],
          selected: false,
          selectedDefinitionIds: [],
        });
      }

      if (fields.length) {
        farms.push({ farmId: farm.id, farmName: farm.name, fields });
      }
    }

    this.blocks = [...this.blocks, { id: seasonId, name: meta.name, farms, available }];
  }

  removeSeason(block: SeasonBlock): void {
    for (const farm of block.farms) {
      for (const field of farm.fields) {
        if (this.claimedBy.get(field.fieldId) === block.id) {
          this.claimedBy.delete(field.fieldId);
        }
      }
    }
    this.blocks = this.blocks.filter(b => b.id !== block.id);
  }

  // === Choosing fields ===

  toggleField(block: SeasonBlock, field: SeasonField): void {
    if (this.claimedElsewhere(block, field)) {
      return;
    }

    field.selected = !field.selected;

    if (field.selected) {
      this.claimedBy.set(field.fieldId, block.id);
      // Everything by default. A multi-cultivar field can then be narrowed.
      field.selectedDefinitionIds = field.definitions.map(d => d.id);
    } else {
      this.claimedBy.delete(field.fieldId);
      field.selectedDefinitionIds = [];
    }
  }

  toggleFarm(block: SeasonBlock, farm: FarmGroup): void {
    const selectable = farm.fields.filter(f => !this.claimedElsewhere(block, f));
    const turningOn = !selectable.every(f => f.selected);

    for (const field of selectable) {
      if (field.selected !== turningOn) {
        this.toggleField(block, field);
      }
    }
  }

  allSelected(block: SeasonBlock, farm: FarmGroup): boolean {
    const selectable = farm.fields.filter(f => !this.claimedElsewhere(block, f));
    return selectable.length > 0 && selectable.every(f => f.selected);
  }

  someSelected(block: SeasonBlock, farm: FarmGroup): boolean {
    const selected = farm.fields.filter(f => f.selected).length;
    return selected > 0 && !this.allSelected(block, farm);
  }

  /** True when another season on this recommendation already has the field. */
  claimedElsewhere(block: SeasonBlock, field: SeasonField): boolean {
    const owner = this.claimedBy.get(field.fieldId);
    return owner != null && owner !== block.id;
  }

  claimedByName(field: SeasonField): string {
    const owner = this.claimedBy.get(field.fieldId);
    return this.blocks.find(b => b.id === owner)?.name ?? '';
  }

  selectedCount(block: SeasonBlock): number {
    return block.farms.reduce((total, farm) => total + farm.fields.filter(f => f.selected).length, 0);
  }

  // === Cultivars ===

  isMultiCultivar(field: SeasonField): boolean {
    return field.definitions.length > 1;
  }

  isDefinitionSelected(field: SeasonField, definitionId: number): boolean {
    return field.selectedDefinitionIds.includes(definitionId);
  }

  toggleDefinition(field: SeasonField, definitionId: number): void {
    field.selectedDefinitionIds = this.isDefinitionSelected(field, definitionId)
      ? field.selectedDefinitionIds.filter(id => id !== definitionId)
      : [...field.selectedDefinitionIds, definitionId];
  }

  /** A selected field with every cultivar unticked contributes nothing and must say so. */
  hasNothingChosen(field: SeasonField): boolean {
    return field.selected && field.definitions.length > 0 && field.selectedDefinitionIds.length === 0;
  }

  // === Creating ===

  get totalSelected(): number {
    return this.blocks.reduce((total, block) => total + this.selectedCount(block), 0);
  }

  get canCreate(): boolean {
    return this.totalSelected > 0 && !this.creating;
  }

  /**
   * Builds exactly the payload the existing screen builds.
   *
   * Nothing behind this changes, which is the point: the rearrangement is of the question being
   * asked, not of what is stored.
   */
  create(): void {
    if (!this.canCreate) {
      return;
    }
    this.creating = true;

    const fieldIds: number[] = [];
    const fieldSeasons: Record<number, number> = {};
    const fieldDefinitions: Record<number, number[]> = {};

    for (const block of this.blocks) {
      for (const farm of block.farms) {
        for (const field of farm.fields) {
          if (!field.selected) {
            continue;
          }
          fieldIds.push(field.fieldId);
          fieldSeasons[field.fieldId] = block.id;
          if (field.selectedDefinitionIds.length) {
            fieldDefinitions[field.fieldId] = field.selectedDefinitionIds;
          }
        }
      }
    }

    this.service
      .createSoilCropNutritionWithSeasons(this.client?.id, fieldIds, fieldSeasons, fieldDefinitions)
      .subscribe({
        next: (rec: any) => this.router.navigate(['/soil-crop-nutrition', rec.id]),
        error: () => (this.creating = false),
      });
  }

  trackSeason(_index: number, block: SeasonBlock): number {
    return block.id;
  }

  trackField(_index: number, field: SeasonField): number {
    return field.fieldId;
  }
}
